import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'components/ui/form';
import { Label } from 'components/ui/label';
import { useToast } from 'hooks/use-toast';
import {
  useConnectTool,
  useSaveToolCredentials,
  useStartToolOidc,
} from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { useMcpServiceChatStore } from 'state/store/chat/use-mcp-service-chat-store';
import { useChatStore } from 'state/store/chat/use-chat-store';
import {
  OwnToolClientSection,
  serverErrorCode,
  serverErrorText,
  TOOL_CLIENT_MISSING,
  USER_CLIENT_REQUIRED,
} from './OwnToolClientSection';

interface McpServiceAuthFormData {
  username: string;
  password: string;
  apiToken: string;
  clientId: string;
  clientSecret: string;
}

interface CredentialField {
  name: keyof McpServiceAuthFormData;
  labelKey: string;
  label: string;
  requiredKey: string;
  required: string;
  placeholderKey: string;
  placeholder: string;
  masked: boolean;
  /** Left empty by a user whose setup has no such value; not sent when empty. */
  optional?: boolean;
}

const EMPTY_FORM: McpServiceAuthFormData = {
  username: '',
  password: '',
  apiToken: '',
  clientId: '',
  clientSecret: '',
};

const USERNAME_FIELD: CredentialField = {
  name: 'username',
  labelKey: 'USERNAME',
  label: 'Username',
  requiredKey: 'USERNAME_REQUIRED',
  required: 'Username is required',
  placeholderKey: 'ENTER_USERNAME',
  placeholder: 'Enter username',
  masked: false,
};

const PASSWORD_FIELD: CredentialField = {
  name: 'password',
  labelKey: 'PASSWORD',
  label: 'Password',
  requiredKey: 'PASSWORD_REQUIRED',
  required: 'Password is required',
  placeholderKey: 'ENTER_PASSWORD',
  placeholder: 'Enter password',
  masked: true,
};

const CLIENT_ID_FIELD: CredentialField = {
  name: 'clientId',
  labelKey: 'CLIENT_ID',
  label: 'Client ID',
  requiredKey: 'CLIENT_ID_REQUIRED',
  required: 'Client ID is required',
  placeholderKey: 'ENTER_CLIENT_ID',
  placeholder: 'Enter client ID',
  masked: false,
};

const CLIENT_SECRET_FIELD: CredentialField = {
  name: 'clientSecret',
  labelKey: 'CLIENT_SECRET',
  label: 'Client Secret',
  requiredKey: 'CLIENT_SECRET_REQUIRED',
  required: 'Client Secret is required',
  placeholderKey: 'ENTER_CLIENT_SECRET',
  placeholder: 'Enter client secret',
  masked: true,
};

/**
 * Fields the prompt collects for each `authMethod`. "password" signs in at the tool's
 * login endpoint; the others are the user's own credentials, saved for them as entered.
 */
const CREDENTIAL_FIELDS: Record<string, CredentialField[]> = {
  password: [USERNAME_FIELD, PASSWORD_FIELD],
  basic: [USERNAME_FIELD, PASSWORD_FIELD],
  api_key: [
    {
      name: 'apiToken',
      labelKey: 'API_KEY_OR_TOKEN',
      label: 'API key or token',
      requiredKey: 'API_KEY_OR_TOKEN_REQUIRED',
      required: 'API key or token is required',
      placeholderKey: 'ENTER_API_KEY_OR_TOKEN',
      placeholder: 'Enter API key or token',
      masked: true,
    },
  ],
  client_credentials: [CLIENT_ID_FIELD, CLIENT_SECRET_FIELD],
  // The user's own app registration at the identity provider; an app without a
  // secret leaves that field empty.
  oidc_client: [
    CLIENT_ID_FIELD,
    {
      ...CLIENT_SECRET_FIELD,
      labelKey: 'CLIENT_SECRET_IF_ANY',
      label: 'Client Secret (if your app has one)',
      optional: true,
    },
  ],
};

/**
 * Opens when the chat stream emits `auth_required` for a service-tool.
 * `authMethod === "oidc"` opens the IdP authorization URL in a popup and waits for the
 * callback `postMessage`. "password" signs in with a username and password. "api_key",
 * "basic" and "client_credentials" collect the user's own credentials for the tool and
 * save them for that user only; "oidc_client" does the same for the app registration a
 * user signs in with (MCP servers that cannot register a client, and API tools whose owner
 * allows users' own clients and set none). An API tool otherwise uses the one client its
 * owner set; a missing one is answered `tool_client_missing` by the sign-in start, shown here
 * as an owner-setup message. Where the owner allows it, "oidc" and "password" also show the
 * user's own client ({@link OwnToolClientSection}): a checkbox to use it instead of the
 * configured one, or the fields alone when the sign-in answers `user_client_required`.
 * On success the pending chat turn is auto-resumed.
 */
export function McpServiceAuthModal() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const pendingAuth = useMcpServiceChatStore((state) => state.pendingAuth);
  const clearPendingAuth = useMcpServiceChatStore((state) => state.clearPendingAuth);
  const { mutateAsync: connectTool, isPending: isConnecting } = useConnectTool();
  const { mutateAsync: saveToolCredentials, isPending: isSaving } = useSaveToolCredentials();
  const { mutateAsync: startToolOidc, isPending: isStartingOidc } = useStartToolOidc();
  const activeConversationId = useChatStore((state) => state.activeConversationId);

  const authMethod = pendingAuth?.authMethod || 'password';
  const isOidc = authMethod === 'oidc';
  // "password" mints a session at the tool's login endpoint; every other method
  // stores the entered credentials for this user.
  const isLogin = authMethod === 'password';
  const fields = CREDENTIAL_FIELDS[authMethod] || CREDENTIAL_FIELDS.password;
  const isPending = isConnecting || isSaving;

  const form = useForm<McpServiceAuthFormData>({
    mode: 'onChange',
    defaultValues: EMPTY_FORM,
  });
  const { control, handleSubmit, reset } = form;
  // Set when the sign-in start answers that the tool owner has not set up its client.
  const [ownerSetupRequired, setOwnerSetupRequired] = useState(false);
  // Set when the sign-in start or login answers that the user has to bring their own client.
  const [userClientRequired, setUserClientRequired] = useState(false);

  useEffect(() => {
    if (pendingAuth) {
      reset(EMPTY_FORM);
    }
    setOwnerSetupRequired(false);
    setUserClientRequired(false);
  }, [pendingAuth, reset]);

  // OIDC: listen for the callback page postMessage, then auto-resume the pending turn.
  useEffect(() => {
    if (!pendingAuth || !isOidc) return;

    const onMessage = (event: MessageEvent) => {
      const data = event.data as
        | { type?: string; ok?: boolean; toolId?: string; error?: string }
        | undefined;
      if (data?.type !== 'mcp-oauth') return;
      if (data.toolId && data.toolId !== pendingAuth.mcpServiceId) return;
      if (!data.ok) {
        toast({
          variant: 'destructive',
          title: t('CONNECTION_FAILED') || 'Connection failed',
          description: data.error || t('SOMETHING_WENT_WRONG') || 'Something went wrong.',
        });
        return;
      }
      void afterConnected();
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAuth, isOidc]);

  if (!pendingAuth) {
    return null;
  }

  const resumePendingTurn = async () => {
    const { resume } = useMcpServiceChatStore.getState();
    console.log('[MCP_RESUME_DEBUG] modal afterConnected — resume defined?', typeof resume === 'function');
    clearPendingAuth();
    await resume?.();
  };

  const afterConnected = async () => {
    await queryClient.invalidateQueries({ queryKey: ['tools', 'connection', pendingAuth.mcpServiceId] });
    toast({
      variant: 'success',
      title: t('CONNECTED') || 'Connected',
      description:
        t('MCP_SERVICE_CONNECTED_SUCCESS', {
          name: pendingAuth.mcpServiceName,
          defaultValue: 'Connected to {{name}}.',
        }) || 'Connected.',
    });
    await resumePendingTurn();
  };

  const afterSaved = async () => {
    toast({
      variant: 'success',
      title: t('CREDENTIALS_SAVED') || 'Credentials saved',
      description:
        t('TOOL_CREDENTIALS_SAVED_SUCCESS', {
          name: pendingAuth.mcpServiceName,
          defaultValue: 'Your credentials for {{name}} are saved.',
        }) || 'Your credentials are saved.',
    });
    await resumePendingTurn();
  };

  const signIn = async (data: McpServiceAuthFormData) => {
    try {
      const response = await connectTool({
        toolId: pendingAuth.mcpServiceId,
        payload: { username: data.username.trim(), password: data.password },
      });

      if (!response.connected) {
        throw new Error(response.error || t('INVALID_CREDENTIALS') || 'Invalid credentials');
      }

      await afterConnected();
    } catch (error) {
      if (serverErrorCode(error) === USER_CLIENT_REQUIRED) {
        setUserClientRequired(true);
        return;
      }
      toast({
        variant: 'destructive',
        title: t('CONNECTION_FAILED') || 'Connection failed',
        description:
          serverErrorText(error) ||
          (error instanceof Error ? error.message : t('INVALID_CREDENTIALS') || 'Invalid credentials.'),
      });
    }
  };

  const saveCredentials = async (data: McpServiceAuthFormData) => {
    try {
      // Only the fields this prompt asked for are sent, and an optional one only when filled.
      const payload = Object.fromEntries(
        fields
          .map((field) => [field.name, data[field.name].trim()] as const)
          .filter(([name, value]) => value || !fields.find((field) => field.name === name)?.optional)
      );
      const response = await saveToolCredentials({ toolId: pendingAuth.mcpServiceId, payload });

      if (!response.saved) {
        throw new Error(t('CREDENTIALS_NOT_SAVED') || 'The credentials could not be saved.');
      }

      await afterSaved();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('CREDENTIALS_NOT_SAVED_TITLE') || 'Not saved',
        description:
          serverErrorText(error) || t('CREDENTIALS_NOT_SAVED') || 'The credentials could not be saved.',
      });
    }
  };

  // The event carries only authMethod; fetch the IdP URL from the start endpoint.
  const openOidcPopup = async () => {
    try {
      const { authorizationUrl } = await startToolOidc({
        toolId: pendingAuth.mcpServiceId,
        conversationId: pendingAuth.conversationId || activeConversationId || undefined,
      });
      if (!authorizationUrl) {
        throw new Error(t('SOMETHING_WENT_WRONG') || 'Something went wrong.');
      }
      const popup = window.open(authorizationUrl, 'mcp-oauth', 'width=520,height=680');
      if (!popup) {
        toast({
          variant: 'destructive',
          title: t('POPUP_BLOCKED') || 'Popup blocked',
          description: t('ALLOW_POPUPS_TO_CONNECT') || 'Please allow popups to connect.',
        });
      }
    } catch (error) {
      if (serverErrorCode(error) === TOOL_CLIENT_MISSING) {
        setOwnerSetupRequired(true);
        return;
      }
      if (serverErrorCode(error) === USER_CLIENT_REQUIRED) {
        setUserClientRequired(true);
        return;
      }
      toast({
        variant: 'destructive',
        title: t('CONNECTION_FAILED') || 'Connection failed',
        description:
          error instanceof Error ? error.message : t('SOMETHING_WENT_WRONG') || 'Something went wrong.',
      });
    }
  };

  const loginTitle =
    t('CONNECT_TO_MCP_SERVICE', {
      name: pendingAuth.mcpServiceName,
      defaultValue: 'Connect to {{name}}',
    }) || 'Connect';
  const credentialsTitle =
    t('ENTER_TOOL_CREDENTIALS', {
      name: pendingAuth.mcpServiceName,
      defaultValue: 'Your credentials for {{name}}',
    }) || 'Your credentials';

  const description = () => {
    if (isOidc && ownerSetupRequired) {
      return (
        t('TOOL_OWNER_SETUP_REQUIRED') || "The tool owner needs to set up this tool's sign-in first."
      );
    }
    if (isOidc) {
      return (
        t('MCP_SERVICE_OIDC_REQUIRED_DESCRIPTION') ||
        'This service uses single sign-on. Continue to your identity provider to authorize access.'
      );
    }
    if (isLogin) {
      return (
        t('MCP_SERVICE_AUTH_REQUIRED_DESCRIPTION') ||
        'This service needs your credentials before the assistant can continue. They are sent securely and never shared with the model.'
      );
    }
    if (authMethod === 'oidc_client') {
      return (
        t('TOOL_OIDC_CLIENT_REQUIRED_DESCRIPTION') ||
        'We are initiating a secure session. This tool signs you in with your own app registration at its identity provider. Enter its client ID, and its client secret if it has one. They are saved for you only and never shared with the model. You will be asked to sign in next.'
      );
    }
    return (
      t('TOOL_CREDENTIALS_REQUIRED_DESCRIPTION') ||
      'We are initiating a secure session. Enter your own credentials for this tool. They are sent securely, saved for you only, and never shared with the model.'
    );
  };

  const submitLabel = () => {
    if (isLogin) {
      return isPending ? t('CONNECTING') || 'Connecting...' : t('CONNECT') || 'Connect';
    }
    return isPending ? t('SAVING') || 'Saving...' : t('SAVE') || 'Save';
  };

  return (
    <Dialog open onOpenChange={(open) => !open && clearPendingAuth()}>
      <DialogContent className="max-w-[440px] rounded-[0.5rem] p-4">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-lg font-medium leading-none">
            {isOidc || isLogin ? loginTitle : credentialsTitle}
          </DialogTitle>
          <DialogDescription className="text-sm leading-6">{description()}</DialogDescription>
        </DialogHeader>

        {/* The user's own client, where the tool's owner allows one (API tools only). */}
        {(isOidc || isLogin) && !ownerSetupRequired && (
          <OwnToolClientSection
            toolId={pendingAuth.mcpServiceId}
            required={userClientRequired}
            onChange={() => setUserClientRequired(false)}
          />
        )}

        {isOidc ? (
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => clearPendingAuth()}>
              {t('CANCEL') || 'Cancel'}
            </Button>
            {/* Nothing the user can enter fixes a missing tool client, so no sign-in to retry;
                a missing own client is entered above first. */}
            {!ownerSetupRequired && !userClientRequired && (
              <Button type="button" onClick={() => void openOidcPopup()} disabled={isStartingOidc}>
                {isStartingOidc ? t('CONNECTING') || 'Connecting...' : t('CONTINUE_TO_SIGN_IN') || 'Continue to sign in'}
              </Button>
            )}
          </DialogFooter>
        ) : (
          <Form {...form}>
            <form
              onSubmit={handleSubmit(isLogin ? signIn : saveCredentials)}
              className="flex flex-col gap-4 py-2"
            >
              {fields.map((credentialField, index) => (
                <FormField
                  key={credentialField.name}
                  control={control}
                  name={credentialField.name}
                  rules={
                    credentialField.optional
                      ? undefined
                      : { required: t(credentialField.requiredKey) || credentialField.required }
                  }
                  render={({ field }) => (
                    <FormItem>
                      <Label className="text-sm font-medium leading-6">
                        {t(credentialField.labelKey) || credentialField.label}
                        {!credentialField.optional && <span className="text-destructive ml-0.5">*</span>}
                      </Label>
                      <FormControl>
                        <Input
                          {...field}
                          autoFocus={index === 0}
                          type={credentialField.masked ? 'password' : 'text'}
                          className="h-10"
                          placeholder={t(credentialField.placeholderKey) || credentialField.placeholder}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}

              <DialogFooter className="gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => clearPendingAuth()}
                  disabled={isPending}
                >
                  {t('CANCEL') || 'Cancel'}
                </Button>
                <Button type="submit" disabled={isPending}>
                  {submitLabel()}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default McpServiceAuthModal;
